/**
 * Response shapes for Labeeb's admin API — all 16 endpoints.
 *
 * From docs/products/labeeb-api-reference.md. Every field is optional or
 * nullable on purpose: client.ts does a blind `(await resp.json()) as T`, so
 * nothing here is guaranteed at runtime. A truncated or renamed response must
 * degrade through a helper's floor rather than crash a `.map` call site — the
 * failure mode that has bitten this console four times against MCP.
 *
 * Mirrors api/mcp.ts. Helpers are prefixed `labeeb…` where mcp.ts already owns
 * the plain name, so a page can import from both without aliasing.
 */

// ---------------------------------------------------------------- catalog --

/** One selectable tier. Render `display_name`, submit `tier_id` — they differ. */
export interface LabeebTierRef {
  tier_id: string;
  display_name: string;
}

/**
 * Every valid value a write on this API accepts. Fetch once and use it to
 * populate pickers rather than hard-coding lists.
 *
 * `agents` is the ROUTABLE set, not every agent name the product defines —
 * `supervisor` is a real agent key but is not routable, and submitting it is
 * a 422. Everything is optional per the usual `client.ts` cast note: a
 * truncated catalog response should degrade every picker to empty, not crash
 * one.
 */
export interface LabeebCatalog {
  agents?: string[];
  tiers?: LabeebTierRef[];
  tier_limit_attributes?: string[];
  flags?: string[];
  audit_domains?: string[];
  notes?: string[];
}

/** `data.agents`, defaulting to `[]`. */
export function agentsOf(c: LabeebCatalog | undefined | null): string[] {
  return c?.agents ?? [];
}

/** `data.tiers`, defaulting to `[]`. Read through this, never `c.tiers` directly. */
export function tierRefsOf(c: LabeebCatalog | undefined | null): LabeebTierRef[] {
  return c?.tiers ?? [];
}

/**
 * A tier's display name, or the raw id when the catalog hasn't loaded yet or
 * has no matching row — so a tier badge never shows `undefined` while the
 * catalog request is still in flight or has failed.
 */
export function tierDisplayName(c: LabeebCatalog | undefined | null, tierId: string): string {
  return tierRefsOf(c).find((t) => t.tier_id === tierId)?.display_name ?? tierId;
}

// ---------------------------------------------------------------- tenants --

/**
 * A tenant's knobs, as returned by the list route.
 *
 * `GET /admin/tenants` only returns tenants that HAVE a config item — a
 * tenant absent from the list is not missing, it has no explicit tier and no
 * agent narrowing, which is the normal state. This shape carries no `note`
 * field of its own for that; see `LabeebTenantList.note`.
 */
export interface LabeebTenantSummary {
  tenant_id: string;
  /** The stored tier, or `null` when the tenant has no explicit tier set. */
  tier_id: string | null;
  /**
   * The app-level agent ceiling, or `null` when the tenant is not narrowed —
   * distinct from `[]`, which means "narrowed to nothing".
   */
  allowed_agents: string[] | null;
  /**
   * Agents unioned in AFTER the app-ceiling intersection, so this can grant
   * an agent the environment has not made stable. `null` when none are
   * granted; `[]` means explicitly narrowed to none, same distinction as
   * `allowed_agents`.
   */
  beta_agents: string[] | null;
}

export interface LabeebTenantList {
  /**
   * Optional for the usual `client.ts` reason — nothing here is actually
   * guaranteed at runtime. Read through `labeebTenantsOf`, never this field
   * directly.
   */
  tenants?: LabeebTenantSummary[];
  count?: number;
  /** e.g. "Only tenants with a TenantConfigs item…" — surface it, it explains the list's scope. */
  note?: string;
  next_cursor?: string;
}

/**
 * `data.tenants`, defaulting to `[]` rather than throwing on `.filter`/`.map`.
 *
 * NOT named `tenantsOf` — mcp.ts already exports that name for
 * `McpTenantList`, and a page importing both products' tenant helpers must
 * never need to alias one of the two imports.
 */
export function labeebTenantsOf(l: LabeebTenantList | undefined | null): LabeebTenantSummary[] {
  return l?.tenants ?? [];
}

/**
 * One tenant's today-usage counters, read-only.
 *
 * `daily`/`monthly` are `unknown` because the reference doc's only example
 * shows them as `null` (a tenant with no config item) — their populated shape
 * is not documented anywhere the console can rely on. `per_agent_today` is
 * WRITTEN but explicitly NOT ENFORCED per the doc: never sum it into an
 * enforced total, it would overstate what the runtime actually gates on.
 */
export interface LabeebUsage {
  daily: unknown;
  monthly: unknown;
  per_agent_today?: unknown[];
  /** True when there is no write path for this data — always true today. */
  read_only?: boolean;
}

/**
 * One tenant's full detail: knobs plus today's usage.
 *
 * `has_config_item: false` is NOT an error — render it as "on defaults". A
 * tenant id that genuinely does not exist is a 404 from the client, not a
 * `false` here.
 */
export interface LabeebTenantDetail {
  tenant_id: string;
  has_config_item: boolean;
  tier_id: string | null;
  allowed_agents: string[] | null;
  beta_agents: string[] | null;
  /** e.g. "No TenantConfigs item. The tenant is not narrowed…" when `has_config_item` is false. */
  note?: string;
  usage?: LabeebUsage;
}

/**
 * `PATCH /admin/tenant/{tenant_id}` response. Both `before` and `after` are
 * partial snapshots — only the fields the request touched are guaranteed to
 * differ, and the route validates everything before writing anything, so a
 * body with one bad field applies none of it.
 */
export interface LabeebPatchTenantResponse {
  tenant_id: string;
  before: unknown;
  after: unknown;
  /** Which keys actually changed, e.g. `["allowed_agents", "tier_id"]`. */
  changed?: string[];
  /** Per-changed-key delay before the runtime observes it, e.g. `{"TierId": "≤60s …"}`. */
  takes_effect?: Record<string, string>;
  /**
   * Present when `beta_agents` granted an agent the environment has not made
   * stable — `beta_agents` is unioned in AFTER the app-ceiling intersection,
   * so this is easy to reach for by accident. Surface it.
   */
  beta_note?: string;
}

// ------------------------------------------------------------------ tiers --

/**
 * One tier's limits row.
 *
 * `source: "code_default"` means editing this tier CREATES a row rather than
 * changing one — there is no seeded row for it yet, only the code's fallback.
 * `limits: null` accompanies that case in the doc's own example: a
 * `code_default` tier has no stored limits to show.
 */
export interface LabeebTierRow {
  tier_id: string;
  display_name: string;
  source?: "table" | "code_default";
  limits: Record<string, number> | null;
}

export interface LabeebTiers {
  /** Optional per the usual cast note. Read through `tierRowsOf`, never directly. */
  tiers?: LabeebTierRow[];
  editable_attributes?: string[];
  notes?: string[];
}

/** `data.tiers`, defaulting to `[]`. */
export function tierRowsOf(t: LabeebTiers | undefined | null): LabeebTierRow[] {
  return t?.tiers ?? [];
}

/**
 * `PATCH /admin/tiers/{tier_id}` response.
 *
 * `warning` is present when a seed script in the product repo will REVERT
 * this change on its next run — always show it, it is the single most
 * important thing about this response. The shape beyond `tier_id`/`warning`
 * is otherwise unspecified by the doc, so extra keys pass through as
 * `unknown` rather than being dropped or asserted away.
 */
export interface LabeebPatchTierResponse {
  tier_id?: string;
  warning?: string;
  [key: string]: unknown;
}

// ------------------------------------------------------------------ flags --

/**
 * One feature flag's resolved state.
 *
 * `effective` is the field to RENDER, not `stored_value` — a flag with no
 * stored parameter still has a real behaviour, and it differs per flag
 * (`narration_enabled` defaults ON while unset, `narration_haiku_enabled`
 * defaults OFF). `stored_value` is `null` exactly when `exists` is false.
 */
export interface LabeebFlag {
  flag: string;
  exists: boolean;
  stored_value: boolean | null;
  effective: boolean;
  code_default: boolean;
}

export interface LabeebFlags {
  flags?: LabeebFlag[];
  /** e.g. "Takes effect within ~60s…" — surface it, nothing here is instant. */
  takes_effect?: string;
}

/** `data.flags`, defaulting to `[]`. */
export function flagsOf(f: LabeebFlags | undefined | null): LabeebFlag[] {
  return f?.flags ?? [];
}

// ----------------------------------------------------------------- config --

/**
 * One live-config key: an app-level value the runtime re-reads without a
 * deploy. `value` is `unknown` because it varies by key (a string, a list, a
 * number) and a truncated/renamed response must not assert a shape that
 * isn't there. `source` describes where the CURRENT value comes from, e.g.
 * "yaml (deployed)" when `overridden` is false.
 */
export interface LabeebConfigKey {
  key: string;
  writable: boolean;
  overridden: boolean;
  value: unknown;
  source?: string;
  description?: string;
  takes_effect?: string;
  example?: unknown;
}

/**
 * A config key the console cannot make live, with the reason why — e.g.
 * "services_registry.__init__ copies it to self.quota_enabled…". Render
 * these VISIBLE but disabled, with the reason: hiding them loses information
 * an operator needs.
 */
export interface LabeebReadOnlyConfigKey {
  key: string;
  reason: string;
}

export interface LabeebConfig {
  keys?: LabeebConfigKey[];
  read_only?: LabeebReadOnlyConfigKey[];
  /** e.g. "A key with overridden=false is using the value deployed in the yaml…" */
  note?: string;
}

/** `data.keys`, defaulting to `[]`. */
export function configKeysOf(c: LabeebConfig | undefined | null): LabeebConfigKey[] {
  return c?.keys ?? [];
}

/**
 * `data.read_only`, defaulting to `[]`. Independent of `configKeysOf` — a
 * response carrying one list but not the other must not make either throw.
 */
export function readOnlyConfigKeysOf(
  c: LabeebConfig | undefined | null,
): LabeebReadOnlyConfigKey[] {
  return c?.read_only ?? [];
}

// ------------------------------------------------------------------ audit --

/**
 * One admin action against Labeeb's config, flags, quota or tenant knobs.
 *
 * `actor` is nullable for the same reason `mcp.ts`'s `AuditEvent.actor` is:
 * an audit log is the last surface that should fall over, so a bare
 * `.replace`/`.split` on a row missing its actor must be a compile error
 * rather than a white screen. `before`/`after` are `unknown`, not a fixed
 * type, because the value shape is action-shaped — a string for
 * `tenant.set_tier`, a list for a `stable_agents` config write, a boolean for
 * a flag toggle — exactly like MCP's audit rows. Render both through the same
 * `auditValue`/`auditTarget` helpers `mcp.ts` already provides; there is no
 * Labeeb-specific reason for those to differ.
 */
export interface LabeebAuditEvent {
  /**
   * Nullable though the doc's only example has one: the doc gives no prose
   * guarantee that every row carries a timestamp, and MCP's structurally
   * identical (DynamoDB-backed) audit log hit exactly this gap — a real row
   * with no `timestamp` reached a bare `.replace()` inside a `.map` and
   * unmounted the whole React root. Absence of a documented `null` is not
   * proof of non-nullability; widen rather than assume.
   */
  timestamp: string | null;
  domain: string;
  action: string;
  actor: string | null;
  target: unknown;
  before: unknown;
  after: unknown;
}

export interface LabeebAuditPage {
  events?: LabeebAuditEvent[];
  count?: number;
  /** Opaque. Pass back verbatim as `?cursor=`; never construct or parse one. */
  cursor?: string;
}

/**
 * `data.events`, defaulting to `[]`.
 *
 * NOT named `eventsOf` — mcp.ts already exports that name for `AuditPage`,
 * and `Tenants.tsx`/`Tenant.tsx` import audit helpers from both products.
 */
export function labeebEventsOf(p: LabeebAuditPage | undefined | null): LabeebAuditEvent[] {
  return p?.events ?? [];
}
